import usePageContext from '@rover/react-lib/src/context/usePageContext';
import { selectors as countryConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/countryConfiguration.duck';
import { CountryConfiguration, CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest';
import {
  useCurrentCountryConfiguration,
  useNavigationCountryConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/CountryConfiguration';

import useDucks from './useDucks';

export type CountryConfigurationData = {
  current?: CountryConfiguration | null;
  currentUrls?: CountryConfigurationUrls | undefined;
  navigation?: CountryConfiguration | null;
  navigationUrls?: CountryConfigurationUrls | undefined;
  navigationAvailableLanguages?: string[][] | undefined | null;
  navigationIsEU?: boolean | undefined;
  showLanguageSelector?: boolean | undefined;
};

export type CountryConfigurationSelectors = {
  current?: CountryConfiguration | null;
  currentUrls: CountryConfigurationUrls | undefined;
  navigation?: CountryConfiguration | null;
  navigationUrls: CountryConfigurationUrls | undefined;
  navigationAvailableLanguages: string[][] | undefined | null;
  navigationIsEU: boolean;
};

const selector = (state): CountryConfigurationSelectors => ({
  current: countryConfigurationSelectors.getCurrentCountryConfiguration(state),
  currentUrls: countryConfigurationSelectors.getCurrentCountryUrls(state),
  navigation: countryConfigurationSelectors.getNavigationCountryConfiguration(state),
  navigationUrls: countryConfigurationSelectors.getNavigationCountryUrls(state),
  navigationAvailableLanguages:
    countryConfigurationSelectors.getNavigationAvailableLanguages(state),
  navigationIsEU: countryConfigurationSelectors.getNavigationCurrentContinentIsEurope(state),
});

const getShowLanguageSelector = (
  availableLanguages?: string[][] | null,
  suppressLanguagePicker?: boolean
): boolean => {
  const hasAvailableLanguages = !!availableLanguages && availableLanguages.length > 1;
  const showLanguageSelector = !suppressLanguagePicker && hasAvailableLanguages;
  return showLanguageSelector;
};

const useCountryConfigurationShim = (): CountryConfigurationData => {
  const { isReactQueryRoute, suppressLanguagePicker } = usePageContext();

  const { data: currentData, urls: currentUrls } = useCurrentCountryConfiguration({
    enabled: isReactQueryRoute,
  });

  const {
    data: navigationData,
    availableLanguages: navigationAvailableLanguages,
    isEU: navigationIsEU,
    urls: navigationUrls,
  } = useNavigationCountryConfiguration({
    enabled: isReactQueryRoute,
  });
  const { data: selectedData } = useDucks({ selector });

  if (isReactQueryRoute) {
    return {
      current: currentData,
      currentUrls,
      navigationUrls,
      navigation: navigationData,
      navigationAvailableLanguages,
      navigationIsEU,
      showLanguageSelector: getShowLanguageSelector(
        navigationAvailableLanguages,
        suppressLanguagePicker
      ),
    };
  }

  return {
    ...selectedData,
    showLanguageSelector: getShowLanguageSelector(
      selectedData.navigationAvailableLanguages,
      suppressLanguagePicker
    ),
  };
};

export default useCountryConfigurationShim;
